/**
 * The sentence a new subscriber reads the moment their card is charged.
 *
 * IMPORTS NOTHING, on purpose. Same contract as calendarStatsText.js: a module
 * with no imports can be lifted out and evaluated by scripts/check-calendar.js,
 * so the exact words a paying customer sees are asserted by a gate rather than
 * eyeballed once. Copy in a component body is copy nothing can check.
 *
 * WHY THERE IS A BANNER AT ALL. The subscribe form used to do nothing visible
 * on success: the server returned 201 with a session token, the client stored
 * it, and the form stayed on screen. Somebody who had just been charged $24 had
 * no way to tell the charge from a dead button, and the obvious thing to do
 * about a dead button is press it again. The redirect fixes the navigation and
 * this line fixes the confirmation, which are two different halves of the same
 * bug.
 *
 * UTC, NOT THE BROWSER'S ZONE. The renewal date is computed on the server in
 * UTC and arrives as an ISO string. Formatting it in local time shows the
 * previous day to every subscriber west of Greenwich, which for a US product is
 * all of them, and "next charge on the 19th" against a card that is charged on
 * the 20th is the kind of small wrongness that produces a support email.
 */

/** What each plan costs, said the way the subscribe page says it. */
export const CALENDAR_PLAN_PRICE = {
  monthly: '$24',
  annual: '$199'
};

/** A renewal date as a person reads it, or '' when there is not a usable one. */
export function formatChargeDate(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * The banner, degrading one clause at a time.
 *
 * NEVER RETURNS EMPTY. A subscriber whose card was just charged is told so even
 * when the plan or the date cannot be read, because the confirmation is the
 * point and the amount is the detail. A comp account has no price and falls
 * through to the plain sentence rather than to "Your first charge was ".
 */
export function formatWelcomeBanner(subscriber) {
  const plan = subscriber && subscriber.plan ? String(subscriber.plan) : '';
  const price = CALENDAR_PLAN_PRICE[plan] || '';
  const date = formatChargeDate(subscriber && subscriber.currentPeriodEnd);

  if (price && date) return `You're in. Your first charge was ${price}, next charge on ${date}.`;
  if (price) return `You're in. Your first charge was ${price}.`;
  return "You're in. Your subscription is active.";
}
